/**
 * Dispatcher Provider
 *
 * Provider component that handles automatic dispatcher data fetching
 * and Firebase token registration when user logs in
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useAuthStore } from '@/store/authStore'
import { useAutoFetchDispatcherData } from '@/hooks/api/useAuth'
import { useRegisterFirebaseToken } from '@/hooks/api/useNotifications'
import {
  handleGetFirebaseToken,
  requestNotificationPermission,
} from '@/utils/firebaseUtils'
import { toast } from 'react-hot-toast'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { isStagingEnvironment } from '@/utils/routeUtils'
import { setClarityUser, setClarityTag } from '@/config/clarity'
import ShiftDefectExplanationModal from '@/components/shift-defects/ShiftDefectExplanationModal'

const SHIFT_DEFECT_STORAGE_KEY = 'pending-shift-defect'
const ACTIVE_PRECINCT_SWITCH_TOAST_KEY = 'active-precinct-switch-message'

const loadStoredShiftDefect = (dispatcherId?: string) => {
  if (!dispatcherId) return null
  try {
    const raw = localStorage.getItem(SHIFT_DEFECT_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ShiftDefectInfo
    return parsed.dispatcher_id === dispatcherId ? parsed : null
  } catch (error) {
    console.error('Failed to read stored shift defect:', error)
    return null
  }
}

// Dispatcher Context Types
interface DispatcherData {
  dispatcher_id: string
  email: string
  first_name: string
  last_name: string
  profile_image_url?: string
  precinct_id: string
  precinct_name: string
  country?: string
  country_code?: string
  dispatch_center_country?: string
  dispatch_center_country_code?: string
  precinct_country?: string
  precinct_country_code?: string
  country_calling_code?: string
  phone_country_code?: string
  dialing_code?: string
  role?: 'dispatcher' | 'admin' | 'supervisor' | 'member'
  isSupervisor?: boolean
  firebasePermissionToken?: string
}

interface DispatcherContextType {
  dispatcher: DispatcherData | null
  isLoading: boolean
  error: string | null
  updateFirebaseToken: (token: string) => void
  refreshDispatcherData: () => void
  pendingShiftDefect: ShiftDefectInfo | null
}

interface ShiftDefectInfo {
  dispatcher_id: string
  shift_id: string
  defect_type: string
}

// Create Context with safe defaults to avoid crashes during auth transitions
const DispatcherContext = createContext<DispatcherContextType>({
  dispatcher: null,
  isLoading: false,
  error: null,
  updateFirebaseToken: () => {},
  refreshDispatcherData: () => {},
  pendingShiftDefect: null,
})

// Custom hook to use dispatcher context
export const useDispatcher = (): DispatcherContextType => {
  const context = useContext(DispatcherContext)
  return context
}

interface DispatcherProviderProps {
  children: React.ReactNode
}

export const DispatcherProvider: React.FC<DispatcherProviderProps> = ({
  children,
}) => {
  const { user, isAuthenticated } = useAuthStore()
  const registerFirebaseToken = useRegisterFirebaseToken()

  // Local state for dispatcher data and Firebase token
  const [dispatcher, setDispatcher] = useState<DispatcherData | null>(null)
  const [firebaseToken, setFirebaseToken] = useState<string | null>(null)
  const [pendingShiftDefect, setPendingShiftDefect] =
    useState<ShiftDefectInfo | null>(null)

  // Automatically fetch dispatcher data when user logs in
  const {
    data: dispatcherApiData,
    isLoading: isLoadingDispatcher,
    error: dispatcherError,
    refetch: refetchDispatcherData,
  } = useAutoFetchDispatcherData()

  // Update dispatcher state when API data changes
  const resolvedDispatcher = useMemo(() => {
    const data: any = dispatcherApiData as any
    return data?.dispatcher || data?.data?.dispatcher || data || null
  }, [dispatcherApiData])

  useEffect(() => {
    if (resolvedDispatcher && isAuthenticated) {
      const newDispatcherData: DispatcherData = {
        dispatcher_id: resolvedDispatcher.dispatcher_id,
        email: resolvedDispatcher.email,
        first_name: resolvedDispatcher.first_name,
        last_name: resolvedDispatcher.last_name,
        profile_image_url: resolvedDispatcher.profile_image_url,
        precinct_id: resolvedDispatcher.precinct_id,
        precinct_name: resolvedDispatcher.precinct_name,
        country: resolvedDispatcher.country,
        country_code: resolvedDispatcher.country_code,
        dispatch_center_country: resolvedDispatcher.dispatch_center_country,
        dispatch_center_country_code:
          resolvedDispatcher.dispatch_center_country_code,
        precinct_country: resolvedDispatcher.precinct_country,
        precinct_country_code: resolvedDispatcher.precinct_country_code,
        country_calling_code: resolvedDispatcher.country_calling_code,
        phone_country_code: resolvedDispatcher.phone_country_code,
        dialing_code: resolvedDispatcher.dialing_code,
        role: resolvedDispatcher.role,
        isSupervisor: resolvedDispatcher.isSupervisor,
        firebasePermissionToken: firebaseToken || undefined,
      }
      setDispatcher(newDispatcherData)

      // Set Clarity user with enhanced dispatcher information
      const clarityUserId = resolvedDispatcher.email
      const friendlyName = `${resolvedDispatcher.first_name} ${resolvedDispatcher.last_name}`

      setClarityUser(clarityUserId, undefined, undefined, friendlyName)

      // Add custom tags for better session segmentation
      if (resolvedDispatcher.role) {
        setClarityTag('role', resolvedDispatcher.role)
      }
      if (resolvedDispatcher.precinct_name) {
        setClarityTag('precinct', resolvedDispatcher.precinct_name)
      }
      if (resolvedDispatcher.isSupervisor !== undefined) {
        setClarityTag(
          'isSupervisor',
          resolvedDispatcher.isSupervisor.toString()
        )
      }

      // Cache dispatcher data in localStorage for role-based routing
      try {
        localStorage.setItem(
          'dispatcher-data',
          JSON.stringify(newDispatcherData)
        )
        localStorage.setItem('precinctId', resolvedDispatcher.precinct_id)
      } catch (error) {
        console.error('Failed to cache dispatcher data:', error)
      }
    } else if (!isAuthenticated) {
      // Clear dispatcher data when user logs out
      setDispatcher(null)
      setFirebaseToken(null)
      setPendingShiftDefect(null)
      try {
        localStorage.removeItem(SHIFT_DEFECT_STORAGE_KEY)
      } catch (error) {
        console.error('Failed to clear shift defect cache:', error)
      }
      // Clear cached dispatcher data
      try {
        localStorage.removeItem('dispatcher-data')
        localStorage.removeItem('precinctId')
      } catch (error) {
        console.error('Failed to clear dispatcher data cache:', error)
      }
    }
  }, [resolvedDispatcher, isAuthenticated, firebaseToken])

  useEffect(() => {
    if (!isAuthenticated || isLoadingDispatcher) return

    try {
      const message = sessionStorage.getItem(ACTIVE_PRECINCT_SWITCH_TOAST_KEY)
      if (!message) return

      sessionStorage.removeItem(ACTIVE_PRECINCT_SWITCH_TOAST_KEY)
      toast.success(message)
    } catch (error) {
      console.error('Failed to read active precinct switch message:', error)
    }
  }, [isAuthenticated, isLoadingDispatcher])

  useEffect(() => {
    if (!isAuthenticated || !user?.sub) return
    const storedDefect = loadStoredShiftDefect(user.sub)
    if (storedDefect) {
      setPendingShiftDefect(storedDefect)
    }
  }, [isAuthenticated, user?.sub])

  useEffect(() => {
    if (!resolvedDispatcher || !isAuthenticated) return
    const shiftDefect = resolvedDispatcher.shift_defect

    const shiftId = shiftDefect?.shift_id || ''
    const defectType = shiftDefect?.defect_type || 'unknown'

    if (shiftDefect?.defected && shiftId) {
      const defectInfo: ShiftDefectInfo = {
        dispatcher_id: resolvedDispatcher.dispatcher_id,
        shift_id: shiftId,
        defect_type: defectType,
      }
      setPendingShiftDefect(defectInfo)
      try {
        localStorage.setItem(
          SHIFT_DEFECT_STORAGE_KEY,
          JSON.stringify(defectInfo)
        )
      } catch (error) {
        console.error('Failed to persist shift defect:', error)
      }
    } else if (!loadStoredShiftDefect(resolvedDispatcher.dispatcher_id)) {
      setPendingShiftDefect(null)
    }
  }, [resolvedDispatcher, isAuthenticated])

  // Handle Firebase token registration
  useEffect(() => {
    const handleFirebaseTokenRegistration = async () => {
      try {
        // Only proceed if user is authenticated and we have user data
        if (!isAuthenticated || !user?.sub) {
          return
        }

        // Check if notification permission is granted
        if (
          'Notification' in window &&
          window.Notification?.permission !== 'granted' &&
          !isStagingEnvironment
        ) {
          toast(
            t => (
              <span>
                Please enable notifications for full functionality. <br />
                <button
                  onClick={async () => {
                    toast.dismiss(t.id)
                    const granted = await requestNotificationPermission()
                    if (granted) {
                      toast.success('Notifications enabled!')
                      // Retry registration after permission granted
                      handleFirebaseTokenRegistration()
                    } else {
                      toast.error('Notifications permission denied.')
                    }
                  }}
                  style={{
                    // color: '#2563eb',
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Enable Now
                </button>
              </span>
            ),
            { duration: 10000 }
          )
          return
        }

        if (window.Notification?.permission === 'granted') {
          // If no cached token in state, generate and register new one
          if (!firebaseToken) {
            const token = await handleGetFirebaseToken()
            if (token && !isStagingEnvironment) {
              setFirebaseToken(token)
              registerFirebaseToken.mutate({
                entity: 'dispatchers',
                token,
                id: user.sub,
              })
            }
          }
        }
      } catch (error) {
        console.error('Failed to handle Firebase token registration:', error)
      }
    }

    // Only run after dispatcher data is loaded to ensure we have all user info
    if (isAuthenticated && user?.sub && !isLoadingDispatcher) {
      handleFirebaseTokenRegistration()
    }
  }, [
    isAuthenticated,
    user?.sub,
    isLoadingDispatcher,
    registerFirebaseToken,
    firebaseToken,
  ])

  // Handle dispatcher data loading errors
  useEffect(() => {
    if (dispatcherError) {
      console.error('Failed to load dispatcher data:', dispatcherError)
      // toast.error(
      //   'Failed to load user profile. Please try refreshing the page.'
      // )
    }
  }, [dispatcherError])

  // Context methods
  const updateFirebaseToken = (token: string) => {
    setFirebaseToken(token)
  }

  const refreshDispatcherData = () => {
    refetchDispatcherData()
  }

  // Context value
  const contextValue: DispatcherContextType = {
    dispatcher,
    isLoading: isLoadingDispatcher,
    error: dispatcherError?.message || null,
    updateFirebaseToken,
    refreshDispatcherData,
    pendingShiftDefect,
  }

  // Show loading spinner if user is authenticated but dispatcher data is not yet loaded
  if (isAuthenticated && isLoadingDispatcher) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  // Show loading spinner if user is authenticated but dispatcher data is missing
  if (isAuthenticated && !dispatcher && !dispatcherError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  const dispatcherName = resolvedDispatcher?.first_name
    ? `${resolvedDispatcher.first_name} ${resolvedDispatcher.last_name}`
    : dispatcher
      ? `${dispatcher.first_name} ${dispatcher.last_name}`
      : 'Dispatcher'

  const handleShiftDefectSubmitted = () => {
    setPendingShiftDefect(null)
    try {
      localStorage.removeItem(SHIFT_DEFECT_STORAGE_KEY)
    } catch (error) {
      console.error('Failed to clear shift defect cache:', error)
    }
    refetchDispatcherData()
  }

  return (
    <DispatcherContext.Provider value={contextValue}>
      {children}
      {isAuthenticated && pendingShiftDefect && (
        <ShiftDefectExplanationModal
          open
          dispatcherName={dispatcherName}
          dispatcherId={pendingShiftDefect.dispatcher_id}
          shiftId={pendingShiftDefect.shift_id}
          defectType={pendingShiftDefect.defect_type}
          onSubmitted={handleShiftDefectSubmitted}
        />
      )}
    </DispatcherContext.Provider>
  )
}

export default DispatcherProvider
